/**
 * ConfigurationEditModal - Configuration 편집 모달
 * ConfigurationBuilder를 포함하여 Class의 Configuration을 편집
 *
 * Company Admin에서는 Class Configuration만 편집 가능
 * - Class: Product Configuration을 오버라이드
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Settings, Layers, RotateCcw, X } from 'lucide-react'
import { Dialog, DialogContent } from '@quebon/ui/components/dialog'
import type { LessonConfiguration } from '@/types/configuration'
import { createDefaultConfiguration } from '@/types/configuration'
import { ConfigurationBuilder } from './ConfigurationBuilder'

interface ConfigurationEditModalProps {
  targetType: 'class'
  targetId: number
  targetName: string
  configuration: LessonConfiguration | null
  parentConfiguration?: LessonConfiguration | null
  onClose: () => void
  onSave: (config: LessonConfiguration) => Promise<void>
  isSaving?: boolean
}

/**
 * 부모(상품) 설정의 companyEditable 플래그를 config에 반영
 * companyEditable은 항상 상위(Product) 설정에서 결정됨
 */
function applyParentCompanyEditable(
  config: LessonConfiguration,
  parent?: LessonConfiguration | null
): LessonConfiguration {
  if (!parent) return config
  return {
    ...config,
    sequence: config.sequence.map((comp) => {
      const parentComp = parent.sequence.find(
        (p) => p.type === comp.type && p.location === comp.location
      )
      return parentComp
        ? { ...comp, companyEditable: parentComp.companyEditable }
        : comp
    })
  }
}

export function ConfigurationEditModal({
  targetType,
  targetId: _targetId,
  targetName,
  configuration,
  parentConfiguration,
  onClose,
  onSave,
  isSaving = false
}: ConfigurationEditModalProps) {
  const { t } = useTranslation('configuration')
  // _targetId is reserved for future use (e.g., logging, analytics)

  const [currentConfig, setCurrentConfig] = useState<LessonConfiguration>(() => {
    const base = configuration || parentConfiguration || createDefaultConfiguration()
    return applyParentCompanyEditable(base, parentConfiguration)
  })

  const handleSave = async () => {
    const { overrides: _, ...rest } = currentConfig
    const configToSave: LessonConfiguration = {
      ...rest as LessonConfiguration,
      sequence: rest.sequence.map(({ companyEditable: _, ...comp }) => comp)
    }
    await onSave(configToSave)
  }

  const handleReset = () => {
    const base = configuration || parentConfiguration || createDefaultConfiguration()
    setCurrentConfig(applyParentCompanyEditable(base, parentConfiguration))
  }

  const targetTypeLabel = t('editModal.classLabel')
  const TargetIcon = Layers

  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open && !isSaving) onClose() }}>
    <DialogContent className="sm:max-w-4xl p-0 gap-0">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-slate-200 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Settings className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">{t('editModal.title')}</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs font-medium">
                <TargetIcon className="w-3 h-3" />
                {targetTypeLabel}
              </span>
              <span className="text-sm text-slate-500">{targetName}</span>
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          disabled={isSaving}
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6">
        <ConfigurationBuilder
          initialConfiguration={currentConfig}
          onChange={setCurrentConfig}
          showPreview={false}
          showHeader={false}
          parentOverrides={parentConfiguration?.overrides}
        />
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between p-6 border-t border-slate-200 bg-slate-50 flex-shrink-0">
        <button
          onClick={handleReset}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-200 rounded-md transition-colors"
          disabled={isSaving}
        >
          <RotateCcw className="w-4 h-4" />
          {t('editModal.reset')}
        </button>
        <div className="flex items-center gap-3">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
          disabled={isSaving}
        >
          {t('editModal.cancel')}
        </button>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('editModal.saving')}
            </>
          ) : (
            t('editModal.save')
          )}
        </button>
        </div>
      </div>
    </DialogContent>
    </Dialog>
  )
}
